import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataExternalApi, NodeDataUpdateExternalApi, VendorIntegration } from '@onefootprint/request-types/dashboard';
import { Form, type TemplateInputProps } from '@onefootprint/ui';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { ApplyEditMutation } from '../../../base-node';
import type { VendorIntegrationFormData } from '../../types';
import { bodyJsonToInputs, encodeFormKey, getIntegrationInputNodes, getLeafFields } from '../../utils/integration-schema';
import { buildTargetOptions, decodeTarget } from '../../utils/integration-target';
import { createOnSubmitHandler } from '../../utils/submit-handler';
import IntegrationInputsForm from '../integration-inputs-form';
import IntegrationOutputMapping from '../integration-output-mapping';
export const VENDOR_INTEGRATION_FORM_ID = 'vendor-integration-form';
export type VendorIntegrationFormProps = {
  node: {
    id: string;
    data: ApiNodeDataExternalApi;
  };
  mutation: ApplyEditMutation;
  integrations: VendorIntegration[];
  onSubmit?: (data: NodeDataUpdateExternalApi) => void; // For storybook testing
};
const VendorIntegrationForm = t0 => {
  const $ = _c(47);
  const {
    node,
    mutation,
    integrations,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "externalApi"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const config = useGetNodeConfigContext(node.id, "templatable");
  const {
    toggle
  } = useDirty();
  const formMethods = useFormContext();
  if (!formMethods) {
    throw new Error("VendorIntegrationForm must be used within a FormProvider");
  }
  const {
    control,
    formState,
    setValue
  } = formMethods;
  const handleSubmit = createOnSubmitHandler(node, mutation, integrations, onSubmit);
  let t2;
  if ($[1] !== formState.isDirty || $[2] !== toggle) {
    t2 = () => {
      toggle(VENDOR_INTEGRATION_FORM_ID, formState.isDirty);
    };
    $[1] = formState.isDirty;
    $[2] = toggle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== formState.isDirty) {
    t3 = [formState.isDirty];
    $[4] = formState.isDirty;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== control) {
    t4 = {
      control,
      name: "config.integrationTarget"
    };
    $[6] = control;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const integrationTarget = useWatch(t4);
  let t5;
  if ($[8] !== control) {
    t5 = {
      control,
      name: "config.useIntegrationSandboxMock"
    };
    $[8] = control;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const useIntegrationSandboxMock = useWatch(t5);
  const options = buildTargetOptions(integrations);
  const selectedApi = options.find(o => o.value === integrationTarget)?.api;
  const resetApiScopedFields = target => {
    const {
      kind
    } = decodeTarget(target);
    const api = options.find(o_0 => o_0.api.kind === kind)?.api;
    const defaults = bodyJsonToInputs(JSON.stringify(api?.defaultInputs ?? {}), getLeafFields(getIntegrationInputNodes(api?.inputSchema)));
    const outputMappings = {};
    for (const {
      key,
      path
    } of api?.defaultOutputs ?? []) {
      outputMappings[encodeFormKey(path)] = key;
    }
    setValue("config.integrationInputs", defaults, {
      shouldDirty: true
    });
    setValue("config.outputMappings", outputMappings, {
      shouldDirty: true
    });
    setValue("config.integrationMockData", {}, {
      shouldDirty: true
    });
  };
  let t6;
  if ($[10] !== config?.autocomplete) {
    t6 = Object.entries(config?.autocomplete || {}).reduce(_temp2, {} as TemplateInputProps['autocompleteItems']);
    $[10] = config?.autocomplete;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const autocompleteItems = t6;
  const t7 = VENDOR_INTEGRATION_FORM_ID;
  const t8 = "flex flex-col gap-4 pb-4";
  const t9 = formMethods.handleSubmit(handleSubmit);
  const T0 = Section;
  let t10;
  if ($[12] !== t) {
    t10 = t(_temp3);
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t) {
    t11 = t(_temp4);
    $[14] = t;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = Form;
  const t13 = "config.integrationTarget";
  let t14;
  if ($[16] !== t) {
    t14 = t(_temp5);
    $[16] = t;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t) {
    t15 = t(_temp6);
    $[18] = t;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const t16 = "compact";
  const t17 = selectedApi?.description;
  const t18 = options.map(_temp7);
  let t19;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      required: true
    };
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== resetApiScopedFields || $[22] !== t12.Select || $[23] !== t14 || $[24] !== t15 || $[25] !== t17 || $[26] !== t18) {
    t20 = <t12.Select name={t13} label={t14} placeholder={t15} size={t16} hint={t17} onChange={resetApiScopedFields} options={t18} rules={t19} />;
    $[21] = resetApiScopedFields;
    $[22] = t12.Select;
    $[23] = t14;
    $[24] = t15;
    $[25] = t17;
    $[26] = t18;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== autocompleteItems || $[29] !== selectedApi || $[30] !== t || $[31] !== useIntegrationSandboxMock) {
    t21 = selectedApi && <><div className="flex flex-col gap-2"><div className="flex flex-col gap-0.5"><div className="text-label-3 text-primary">{t(_temp8)}</div><div className="text-body-3 text-tertiary">{t(_temp9)}</div></div><IntegrationInputsForm inputSchema={selectedApi.inputSchema} autocompleteItems={autocompleteItems} /></div><Form.Toggle name="config.useIntegrationSandboxMock" label={t(_temp10)} />{useIntegrationSandboxMock && <div className="flex flex-col gap-2"><div className="text-body-3 text-tertiary">{t(_temp11)}</div><IntegrationInputsForm inputSchema={selectedApi.outputSchema} autocompleteItems={autocompleteItems} fieldPrefix="config.integrationMockData" emptyMessageKey="noMockFields" /></div>}</>;
    $[28] = autocompleteItems;
    $[29] = selectedApi;
    $[30] = t;
    $[31] = useIntegrationSandboxMock;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== T0 || $[34] !== t10 || $[35] !== t11 || $[36] !== t20 || $[37] !== t21) {
    t22 = <T0 title={t10} subtitle={t11}>{t20}{t21}</T0>;
    $[33] = T0;
    $[34] = t10;
    $[35] = t11;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== selectedApi || $[40] !== t) {
    t23 = selectedApi && <Section title={t(_temp12)} subtitle={t(_temp13)}><IntegrationOutputMapping outputSchema={selectedApi.outputSchema} /></Section>;
    $[39] = selectedApi;
    $[40] = t;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t22 || $[43] !== t23 || $[44] !== t7 || $[45] !== t9) {
    t24 = <form id={t7} className={t8} onSubmit={t9}>{t22}{t23}</form>;
    $[42] = t22;
    $[43] = t23;
    $[44] = t7;
    $[45] = t9;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
};
const Section = t0 => {
  const $ = _c(10);
  const {
    title,
    subtitle,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="text-label-2 text-primary">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subtitle) {
    t2 = <div className="text-body-2 text-tertiary">{subtitle}</div>;
    $[2] = subtitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-0.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <div className="p-3 border border-tertiary rounded-md flex flex-col gap-5">{t3}{children}</div>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export default VendorIntegrationForm;
function _temp(item) {
  return {
    label: item.token,
    value: item.token,
    detail: item.dataType.join(" | ")
  };
}
function _temp2(acc, t0) {
  const [key_0, value] = t0;
  acc[key_0 === "workflowContextKeys" ? "others" : key_0] = value.map(_temp);
  return acc;
}
function _temp3($) {
  return $.vendorIntegration.title;
}
function _temp4($_0) {
  return $_0.vendorIntegration.subtitle;
}
function _temp5($_1) {
  return $_1.vendorIntegration.integration;
}
function _temp6($_2) {
  return $_2.vendorIntegration.integrationPlaceholder;
}
function _temp7(t0) {
  const {
    value: value_0,
    label
  } = t0;
  return {
    label,
    value: value_0
  };
}
function _temp8($_3) {
  return $_3.vendorIntegration.inputs;
}
function _temp9($_4) {
  return $_4.vendorIntegration.inputsSubtitle;
}
function _temp10($_5) {
  return $_5.vendorIntegration.useMock;
}
function _temp11($_6) {
  return $_6.vendorIntegration.mockDataSubtitle;
}
function _temp12($_7) {
  return $_7.responseConfig.title;
}
function _temp13($_8) {
  return $_8.responseConfig.subtitle;
}